import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map event_type → { color, label, icon JSX }
const EVENT_STYLES = {
  'appointment.created':           { color: 'bg-blue-500',    label: 'Booked',         icon: <PathIcon d="M12 4v16m8-8H4" /> },
  'appointment.created.public':    { color: 'bg-emerald-500', label: 'Booked online',  icon: <PathIcon d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z M3 12h18" /> },
  'appointment.confirmed':         { color: 'bg-green-500',   label: 'Confirmed',      icon: <PathIcon d="M5 13l4 4L19 7" /> },
  'appointment.cancelled':         { color: 'bg-red-500',     label: 'Cancelled',      icon: <PathIcon d="M6 18L18 6M6 6l12 12" /> },
  'appointment.rescheduled':       { color: 'bg-amber-500',   label: 'Rescheduled',    icon: <PathIcon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /> },
  'appointment.status_changed':    { color: 'bg-gray-500',    label: 'Status change',  icon: <PathIcon d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /> },
  'appointment.products_changed':  { color: 'bg-indigo-500',  label: 'Products',       icon: <PathIcon d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /> },
  'appointment.photographer_changed': { color: 'bg-purple-500', label: 'Photographer', icon: <PathIcon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /> },
  'appointment.updated':           { color: 'bg-gray-400',    label: 'Edited',         icon: <PathIcon d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /> },
  'listing.media_delivered':       { color: 'bg-cyan-500',    label: 'Delivered',      icon: <PathIcon d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /> },
  'listing.media_downloaded':     { color: 'bg-sky-500',     label: 'Downloaded',     icon: <PathIcon d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" /> },
  'appointment.payment_received':  { color: 'bg-emerald-600', label: 'Payment',        icon: <PathIcon d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  'appointment.refunded':          { color: 'bg-orange-500',  label: 'Refunded',       icon: <PathIcon d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /> },
  'appointment.payment_reminder_sent': { color: 'bg-yellow-500', label: 'Reminder',   icon: <PathIcon d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /> },
};

function PathIcon({ d }) {
  return (
    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d={d} />
    </svg>
  );
}

const ActivityLog = ({ entityType, entityId, dense = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!entityId) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/activity/${entityType}/${entityId}`);
        if (!cancelled) {
          setRows(Array.isArray(res.data) ? res.data : []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 403) setError('Only owners and admins can view this activity log.');
          else setError('Failed to load activity.');
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [entityType, entityId]);

  if (loading) return <p className="text-sm text-gray-500">Loading activity…</p>;
  if (error) return <p className="text-sm text-gray-500">{error}</p>;
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm border border-dashed border-gray-200 rounded-lg" data-testid="activity-empty">
        No activity yet
      </div>
    );
  }

  return (
    <ol className="relative ml-3 border-l border-gray-200 space-y-4" data-testid={`activity-log-${entityType}`}>
      {rows.map(r => {
        const style = EVENT_STYLES[r.event_type] || { color: 'bg-gray-400', label: 'Event', icon: <PathIcon d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> };
        return (
          <li key={r.id} className="ml-4" data-testid={`activity-row-${r.id}`}>
            <span className={`absolute -left-[11px] flex items-center justify-center w-[22px] h-[22px] rounded-full ${style.color} ring-4 ring-white`}>
              {style.icon}
            </span>
            <div className={`bg-white rounded-lg ${dense ? 'p-2' : 'p-3'} border border-gray-100`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide text-white px-1.5 py-0.5 rounded ${style.color}`}>{style.label}</span>
                  <span className="text-sm text-gray-900 truncate">{r.description}</span>
                </div>
                <time className="text-xs text-gray-500 whitespace-nowrap" title={moment(r.created_at).format('MMM D, YYYY h:mm:ss A z')}>
                  {moment(r.created_at).fromNow()}
                </time>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                by <span className="font-medium text-gray-700">{r.actor_name}</span>
                {r.actor_role && <span className="text-gray-400"> · {r.actor_role}</span>}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default ActivityLog;
